import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import {
	AssignmentLateOutlined,
	DoneAll,
	ScheduleOutlined,
} from "@mui/icons-material";
import { Box, IconButton, Tooltip, Typography, alpha } from "@mui/material";
import { ElementEvaluation, SupplierHeatRow } from "../models/ChemistryDraft";

export const getRevisionReason = (
	heat: SupplierHeatRow,
	elements: ElementEvaluation[] = [],
) => {
	switch (heat.EvaluationCode) {
		case "NO_GRADE":
			return "Grade not assigned: the certificate grade could not be resolved";
		case "NO_SPEC":
			return `Grade ${heat.Grade ?? "-"} has no chemistry ranges`;
		case "NO_CHEMISTRY":
			return "No chemistry loaded for this heat";
		case "OUT_OF_RANGE": {
			const names = elements
				.filter(
					(element) =>
						element.ReportHeatId === heat.ReportHeatId &&
						(element.EvaluationCode === "LOW" ||
							element.EvaluationCode === "HIGH"),
				)
				.map((element) => element.ElementName);

			return names.length > 0
				? `${names.join(", ")} out of range`
				: `${heat.OutOfRangeCount} elements out of range`;
		}
		default:
			// Flagged by the PDF intake: some certificate values could not be read.
			return heat.RevisionRequired
				? "The intake could not read some values of the certificate"
				: null;
	}
};

export const getReviewStatusText = (
	heat: SupplierHeatRow,
	elements: ElementEvaluation[] = [],
) => {
	const status: "ACKNOWLEDGED" | "REVISION_REQUIRED" | "PENDING" =
		heat.AcknowledgedAt != null
			? "ACKNOWLEDGED"
			: getRevisionReason(heat)
				? "REVISION_REQUIRED"
				: "PENDING";

	if (status === "ACKNOWLEDGED")
		return {
			status,
			label: "Acknowledged",
			detail: `By ${heat.AcknowledgedBy ?? "-"} on ${dxtToLocalServerTime(
				heat.AcknowledgedAt,
				"MM/dd/yy HH:mm",
			)}`,
		};

	if (status === "REVISION_REQUIRED")
		return {
			status,
			label: "Revision required",
			detail: getRevisionReason(heat, elements) ?? "",
		};

	return {
		status,
		label: "Pending",
		detail: "No issues detected, not reviewed yet",
	};
};

type Props = {
	heat: SupplierHeatRow;
	canAcknowledge: boolean;
	isAcknowledging: boolean;
	onToggleAcknowledge: (heat: SupplierHeatRow) => void;
	size?: "small" | "medium";
	testId: string;
};

export const AcknowledgeButton = ({
	heat,
	canAcknowledge,
	isAcknowledging,
	onToggleAcknowledge,
	size = "small",
	testId,
}: Props) => {
	const { status, label, detail } = getReviewStatusText(heat);

	const action = canAcknowledge
		? status === "ACKNOWLEDGED"
			? "Click to mark as unacknowledged"
			: "Click to mark as acknowledged"
		: null;

	const color =
		status === "REVISION_REQUIRED"
			? "#B45309"
			: status === "ACKNOWLEDGED"
				? "text.secondary"
				: "text.disabled";

	return (
		<Tooltip
			disableInteractive
			placement="left"
			title={
				<Box>
					<Typography variant="caption" fontWeight={700} display="block">
						{label}
					</Typography>
					<Typography variant="caption" display="block">
						{detail}
					</Typography>
					{action && (
						<Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>
							{action}
						</Typography>
					)}
				</Box>
			}
		>
			<span>
				<IconButton
					size={size}
					disabled={!canAcknowledge || isAcknowledging}
					aria-label={`${label}: ${detail}`}
					data-test-id={testId}
					data-review-status={status}
					onClick={(e) => {
						e.stopPropagation();
						onToggleAcknowledge(heat);
					}}
					sx={{
						color,
						fontSize: size === "small" ? 18 : 22,
						"&.Mui-disabled": { color },
						...(status === "REVISION_REQUIRED" && {
							bgcolor: alpha("#B45309", 0.1),
							"&:hover": { bgcolor: alpha("#B45309", 0.18) },
						}),
					}}
				>
					{status === "ACKNOWLEDGED" ? (
						<DoneAll fontSize="inherit" />
					) : status === "REVISION_REQUIRED" ? (
						<AssignmentLateOutlined fontSize="inherit" />
					) : (
						<ScheduleOutlined fontSize="inherit" />
					)}
				</IconButton>
			</span>
		</Tooltip>
	);
};
